import React, { useEffect, useState } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import { Link } from "react-router-dom";
import Loading from "../utils/Loading";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { JOB_BASE_URL } from "../services/jobService";
import "./JobPortal.css";
const JobPortal = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const [filters, setFilters] = useState({
    location: "",
    employmentType: "",
    remoteType: "",
    searchTerm: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${JOB_BASE_URL}/api/jobs/list`);
        if (!response.ok) {
          throw new Error("Unable to display jobs!");
        }
        const data = await response.json();
        setJobs(data);
        if (data.length > 0) setSelectedJob(data[0]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      location: "",
      employmentType: "",
      remoteType: "",
      searchTerm: "",
    });
    setCurrentPage(1);
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      filters.searchTerm === "" ||
      job.jobTitle?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      job.companyName
        ?.toLowerCase()
        .includes(filters.searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(filters.searchTerm.toLowerCase());

    return (
      matchesSearch &&
      (filters.location === "" ||
        job.location?.city
          ?.toLowerCase()
          .includes(filters.location.toLowerCase()) ||
        job.location?.state
          ?.toLowerCase()
          .includes(filters.location.toLowerCase()) ||
        job.location?.country
          ?.toLowerCase()
          .includes(filters.location.toLowerCase())) &&
      (filters.employmentType === "" ||
        job.employmentType === filters.employmentType) &&
      (filters.remoteType === "" || job.remoteType === filters.remoteType)
    );
  });

  if (loading) return <Loading />;
  if (error)
    return (
      <div className="container my-5">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger text-center">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / jobsPerPage));
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  // Function to format salary
  const formatSalary = (salary) => {
    if (!salary) return "Not specified";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(salary);
  };

  // Function to get time since posting
  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return format(date, "MMMM d, yyyy");
  };

  return (
    <div className="job-portal-container mb-5">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h1 className="hero-title">Find Your Dream Job</h1>
              <p className="hero-subtitle text-light">
                Discover thousands of job opportunities with all the information
                you need.
              </p>

              {/* Search Bar */}
              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Job title, company, or keywords"
                    name="searchTerm"
                    value={filters.searchTerm}
                    onChange={handleFilterChange}
                  />
                  <button
                    className="btn btn-primary search-button"
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="fa fa-search me-2"></i>
                    Search Jobs
                  </button>
                </div>
                <div className="mt-2">
                  <button
                    className="btn btn-link filter-toggle"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <i className={`fa fa-filter me-1`}></i>
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-5 d-none d-lg-block">
              <img
                src="/images/job-search.svg"
                alt="Job Search"
                className="img-fluid hero-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="main-content">
        <div className="container">
          {/* Advanced Filters */}
          <div className={`advanced-filters ${showFilters ? "show" : ""}`}>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="filter-group">
                  <label>Location</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa fa-map-marker"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="City, state, or country"
                      name="location"
                      value={filters.location}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="filter-group">
                  <label>Job Type</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa fa-briefcase"></i>
                    </span>
                    <select
                      className="form-select"
                      name="employmentType"
                      value={filters.employmentType}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Types</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="filter-group">
                  <label>Work Setting</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa fa-home"></i>
                    </span>
                    <select
                      className="form-select"
                      name="remoteType"
                      value={filters.remoteType}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Settings</option>
                      <option value="Remote">Remote</option>
                      <option value="On-site">On-site</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="filter-group">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={clearFilters}
                  >
                    <i className="fa fa-times me-2"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Stats */}
          <div className="results-stats">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h4 className="results-count">
                  {filteredJobs.length}{" "}
                  {filteredJobs.length === 1 ? "job" : "jobs"} found
                </h4>
              </div>
              <div className="col-md-6 text-md-end">
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            </div>
          </div>

          {/* Job Listings and Details */}
          <div className="row">
            {/* Job Listings */}
            <div className="col-lg-5">
              <div className="job-listings-container">
                {filteredJobs.length === 0 ? (
                  <div className="no-results">
                    <div className="no-results-icon">
                      <i className="fa fa-search"></i>
                    </div>
                    <h3>No jobs found</h3>
                    <p>Try adjusting your search criteria or clear filters</p>
                    <button
                      className="btn btn-outline-primary mt-3"
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="job-cards">
                      {currentJobs.map((job) => (
                        <div
                          key={job._id}
                          className={`job-card ${
                            selectedJob?._id === job._id ? "selected" : ""
                          }`}
                          onClick={() => setSelectedJob(job)}
                        >
                          <div className="job-card-header">
                            <div className="company-logo">
                              {job.companyName?.charAt(0) || "C"}
                            </div>
                            <div className="job-card-title">
                              <h3>{job.jobTitle}</h3>
                              <h4>{job.companyName}</h4>
                            </div>
                          </div>

                          <div className="job-card-body">
                            <div className="job-meta">
                              <span className="job-location">
                                <i className="fa fa-map-marker"></i>
                                {job.location?.city}, {job.location?.state}
                              </span>
                              <span className="job-type">
                                <i className="fa fa-briefcase"></i>
                                {job.employmentType}
                              </span>
                              <span className="job-setting">
                                <i className="fa fa-home"></i>
                                {job.remoteType}
                              </span>
                            </div>

                            <div className="job-salary">
                              <i className="fa fa-money" aria-hidden="true"></i>{" "}
                              {formatSalary(job.salary)}
                            </div>
                          </div>

                          <div className="job-card-footer">
                            <span className="job-date">
                              <i className="fa fa-clock"></i>
                              {getTimeSince(job.createdAt)}
                            </span>
                            <button className="btn btn-sm btn-outline-primary view-btn">
                              View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="pagination-container">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                        >
                          <i className="fa fa-chevron-left"></i>
                        </button>

                        <div className="page-numbers">
                          {[...Array(totalPages).keys()].map((number) => (
                            <button
                              key={number + 1}
                              className={`btn btn-sm ${
                                currentPage === number + 1
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => setCurrentPage(number + 1)}
                            >
                              {number + 1}
                            </button>
                          ))}
                        </div>

                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            setCurrentPage(
                              Math.min(totalPages, currentPage + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                        >
                          <i className="fa fa-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Job Details */}
            <div className="col-lg-7">
              {selectedJob && filteredJobs.length > 0 ? (
                <div className="job-details-container">
                  <div className="job-details-header">
                    <div className="company-logo-lg">
                      {selectedJob.companyName?.charAt(0) || "C"}
                    </div>
                    <div className="job-details-title">
                      <h2>{selectedJob.jobTitle}</h2>
                      <h3>{selectedJob.companyName}</h3>
                      <div className="job-details-meta">
                        <span className="job-location">
                          <i className="fa fa-map-marker"></i>
                          {selectedJob.location?.city},{" "}
                          {selectedJob.location?.state},{" "}
                          {selectedJob.location?.country}
                        </span>
                        <span className="job-posted">
                          <i className="fa fa-calendar"></i>
                          Posted {getTimeSince(selectedJob.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="job-actions">
                      <Link
                        to={`/api/jobs/job-details/${
                          selectedJob._id
                        }/${selectedJob.jobTitle.replace(/\s+/g, "-")}`}
                        className="btn btn-primary"
                      >
                        Apply Now
                      </Link>
                      <button className="btn btn-outline-primary ms-2">
                        <i className="fa fa-bookmark"></i>&nbsp; Save
                      </button>
                    </div>
                  </div>

                  <div className="job-highlights">
                    <div className="highlight-item">
                      <div className="highlight-icon">
                        <i className="fa fa-briefcase"></i>
                      </div>
                      <div className="highlight-content">
                        <h4>Job Type</h4>
                        <p>{selectedJob.employmentType}</p>
                      </div>
                    </div>

                    <div className="highlight-item">
                      <div className="highlight-icon">
                        <i className="fa fa-home"></i>
                      </div>
                      <div className="highlight-content">
                        <h4>Work Setting</h4>
                        <p>{selectedJob.remoteType}</p>
                      </div>
                    </div>

                    <div className="highlight-item">
                      <div className="highlight-icon">
                        <i className="fa fa-money" aria-hidden="true"></i>{" "}
                      </div>
                      <div className="highlight-content">
                        <h4>Salary</h4>
                        <p>{formatSalary(selectedJob.salary)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="job-description">
                    <h3>Job Description</h3>
                    <div className="description-content">
                      <ReactMarkdown>
                        {selectedJob.description || "No description provided."}
                      </ReactMarkdown>
                    </div>
                  </div>

                  <div className="job-details-footer">
                    <Link
                      to={`/api/jobs/job-details/${
                        selectedJob._id
                      }/${selectedJob.jobTitle.replace(/\s+/g, "-")}`}
                      className="btn btn-lg btn-primary"
                    >
                      Apply for this position
                    </Link>
                    <div className="share-buttons">
                      <span>Share:</span>
                      <button className="btn btn-sm btn-outline-secondary">
                        <i className="fa fa-linkedin"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <i className="fa fa-twitter"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <i className="fa fa-envelope"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-job-selected">
                  <div className="no-job-icon">
                    <i className="fa fa-briefcase"></i>
                  </div>
                  <h3>No job selected</h3>
                  <p>Select a job from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobPortal;
